import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Building2, Scale, User, Users } from "lucide-react";
import Reveal from "@/components/Reveal";

const PATHWAYS = [
  {
    id: "individual",
    icon: User,
    title: "Individual Use",
    body: [
      "Purchasing a book or workbook allows readers to use their copy for personal reading, reflection, family conversations, and individual study. Readers may also share or lend their purchased physical copy. Reproducing substantial portions, scanning entire workbooks, or distributing unauthorized copies requires written permission or an appropriate license, except as otherwise permitted by law.",
      "Free PDF guides may be downloaded and printed at no cost for personal reflection, family conversations, and non-commercial educational use. They may not be sold, modified, republished, or commercially redistributed without written permission. Please share the official website link when recommending these resources to others.",
    ],
    links: [
      { label: "Books & Workbooks", href: "/books" },
      { label: "Free Resources", href: "/free-resources" },
    ],
  },
  {
    id: "group",
    icon: Users,
    title: "Group Use",
    body: [
      "Reading or studying together? Discounted group copies of the books and workbooks can be arranged for book clubs, support groups, ministries, classrooms, and hospice teams — so every participant has a copy and the author's work is honored.",
    ],
    links: [{ label: "Request group copies", href: "/group-copies" }],
  },
  {
    id: "certification",
    icon: BadgeCheck,
    title: "Facilitator Certification",
    body: [
      "Certification is for individuals who wish to guide others through the material. It follows a six-step pathway, requires an 80% final assessment, and is granted for 12 months with annual renewal.",
      "Certification authorizes a facilitator to lead approved program activities within the scope of the certification terms. It does not grant an organization reproduction rights.",
    ],
    links: [
      { label: "Certification Requirements", href: "/facilitators/certification" },
      { label: "Facilitator Renewal", href: "/facilitators/renewal" },
    ],
  },
  {
    id: "organizations",
    icon: Building2,
    title: "Organizational Licensing",
    body: [
      "A facilitator is certified. An organization is licensed.",
      "Organizations bringing the program to their teams — single-site, multi-site, or enterprise/system — license it through a 12-month organizational cycle, with rights scoped to the licensed level.",
    ],
    links: [
      { label: "Organizational Licensing", href: "/facilitators/organizations" },
    ],
  },
];

export default function Licensing() {
  return (
    <div className="bg-ivory" data-testid="licensing-page">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-16 sm:pt-44">
        <Reveal>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
            Licensing &amp; Permissions
          </h1>
          <p className="mt-6 font-serif italic text-lg sm:text-xl text-gold max-w-3xl">
            Using and sharing From Diagnosis to Goodbye&trade; resources with
            integrity.
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>
              From Diagnosis to Goodbye&trade; books, workbooks, and free
              guides are protected by copyright. This page explains the
              pathways for individual use, group study, facilitator
              certification, and organizational licensing — so that every
              resource is used with the care it was created with.
            </p>
          </div>
        </Reveal>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="licensing-pathways"
      >
        <div className="grid gap-6 md:grid-cols-2">
          {PATHWAYS.map((p, i) => (
            <Reveal key={p.id} delay={0.06 * i} className="h-full">
              <div
                className="flex flex-col h-full bg-white border border-line rounded-md p-8 sm:p-10 hover:shadow-[0_24px_48px_-24px_rgba(11,21,34,0.25)] transition-shadow duration-300"
                data-testid={`licensing-card-${p.id}`}
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-navy-deep">
                    <p.icon className="w-5 h-5 text-gold" />
                  </span>
                  <h2 className="font-serif text-xl sm:text-2xl text-navy">
                    {p.title}
                  </h2>
                </div>
                <div className="mt-5 space-y-4 text-sm sm:text-base leading-relaxed text-stone2 flex-1">
                  {p.body.map((t, j) => (
                    <p key={j}>{t}</p>
                  ))}
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  {p.links.map((l) => (
                    <Link
                      key={l.href + l.label}
                      to={l.href}
                      data-testid={`licensing-link-${p.id}-${l.href.replace(/\//g, "-")}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-gold-hover hover:text-navy transition-colors duration-200"
                    >
                      {l.label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-3xl" data-testid="licensing-copyright">
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
              Copyright &amp; Permission Requests
            </h2>
            <div className="mt-6 space-y-5 text-stone2 leading-relaxed">
              <p>
                &copy; {new Date().getFullYear()} Chaplain Derrick Hall. All
                rights reserved. From Diagnosis to Goodbye&trade; publications
                and educational resources are protected by copyright.
                Unauthorized reproduction, redistribution, commercial use, or
                adaptation requires written permission, except where otherwise
                permitted by law. From Diagnosis to Goodbye&trade; is a
                trademark, with application pending.
              </p>
              <p>
                To request permission for a use not described on this page
                &mdash; excerpts, translations, training partnerships, or
                media use &mdash; please send a message through{" "}
                <Link
                  to="/ask?topic=copyright-permissions"
                  data-testid="licensing-link-ask"
                  className="font-medium text-gold-hover hover:text-navy transition-colors duration-200"
                >
                  Ask Chaplain Derrick
                </Link>
                .
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="bg-navy-deep rounded-md p-10 sm:p-16 text-center">
            <p className="font-serif italic text-lg sm:text-xl text-gold">
              See the person. Hear the voice. Honor the journey.
            </p>
            <p className="mt-6 text-xs leading-relaxed text-ivory/50 max-w-xl mx-auto">
              This page describes the intended use of these resources in good
              faith. Formal license terms are set by the applicable license
              agreement.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
